import React, { useState, useRef, useEffect } from 'react';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DataLoadingSpinner } from './DataLoadingSpinner';
import type { EntityTypeUnion } from '@einhaus/project314-types';

interface EntitySearchSelectProps {
    label: string;
    value: { entityId: number; entityType: EntityTypeUnion['entityType'] } | null;
    onChange: (entity: { entityId: number; entityType: EntityTypeUnion['entityType']; symbol?: string; name?: string } | null) => void;
    entityTypeFilter?: EntityTypeUnion['entityType'] | null;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    error?: string;
    id?: string;
}

const EntitySearchSelect: React.FC<EntitySearchSelectProps> = ({
    label,
    value,
    onChange,
    entityTypeFilter,
    disabled = false,
    placeholder = 'Search for an entity...',
    className = '',
    error,
    id
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Search query
    const searchQuery = useAppQuery({
        ...trpc.search.symbolSearch.queryOptions({ term: debouncedSearchTerm }),
        enabled: debouncedSearchTerm.length >= 2 && !disabled,
        staleTime: 30000
    });

    // Filter results by entity type if specified
    const filteredResults =
        searchQuery.data?.results.filter((result) => {
            if (!entityTypeFilter) return true;
            return result.entityType === entityTypeFilter;
        }) || [];

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen || filteredResults.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) => (prev + 1) % filteredResults.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => (prev - 1 + filteredResults.length) % filteredResults.length);
                break;
            case 'Enter':
                e.preventDefault();

                if (selectedIndex >= 0 && selectedIndex < filteredResults.length) {
                    handleSelect(filteredResults[selectedIndex]);
                }

                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
        }
    };

    const handleSelect = (result: (typeof filteredResults)[0]) => {
        onChange({
            entityId: result.entityId,
            entityType: result.entityType,
            symbol: result.symbol,
            name: result.name
        });

        // Clear the search input after selection
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setIsOpen(false);
        setSelectedIndex(-1);
    };

    const handleClear = () => {
        onChange(null);
        setSearchTerm('');
        setDebouncedSearchTerm('');
        inputRef.current?.focus();
    };

    // Clear search term only when value changes from something to null
    useEffect(() => {
        if (!value) {
            // Only clear if we previously had a value selected
            // Don't clear while user is typing to search
        }
    }, [value]);

    const inputId = id || `entity-search-${label.toLowerCase().replace(/\s+/g, '-')}`;

    return (
        <div className={`relative ${className}`}>
            <label className="block mb-1 text-sm font-medium" htmlFor={inputId}>
                {label}
            </label>
            <div className="relative">
                <input
                    autoComplete="off"
                    className={`w-full px-3 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        value ? 'pr-8' : 'pr-3'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${error ? 'border-red-500' : 'border-gray-700'}`}
                    disabled={disabled}
                    id={inputId}
                    placeholder={placeholder}
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => {
                        if (searchTerm.length >= 2) {
                            setIsOpen(true);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                />
                {value && (
                    <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                        disabled={disabled}
                        type="button"
                        onClick={handleClear}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                        </svg>
                    </button>
                )}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

            {/* Dropdown */}
            {isOpen && searchTerm.length >= 2 && (
                <div
                    className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    ref={dropdownRef}
                >
                    {searchQuery.isLoading ? (
                        <div className="p-4">
                            <DataLoadingSpinner text="Searching..." />
                        </div>
                    ) : searchQuery.error ? (
                        <div className="p-4 text-sm text-red-500">Error loading results. Please try again.</div>
                    ) : filteredResults.length === 0 ? (
                        <div className="p-4 text-sm text-gray-400">
                            No entities found{entityTypeFilter ? ` for type "${entityTypeFilter}"` : ''}.
                        </div>
                    ) : (
                        filteredResults.map((result, index) => (
                            <button
                                className={`w-full text-left px-4 py-2 hover:bg-gray-700 transition-colors ${
                                    index === selectedIndex ? 'bg-gray-700' : ''
                                }`}
                                key={`${result.entityId}-${result.entityType}`}
                                type="button"
                                onClick={() => handleSelect(result)}
                                onMouseEnter={() => setSelectedIndex(index)}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-medium">{result.symbol}</span>
                                        <span className="text-sm text-gray-400 ml-2">{result.name}</span>
                                    </div>
                                    <span className="text-xs text-gray-500">{result.entityType}</span>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default EntitySearchSelect;
